import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000/api/v1';

interface QueueMetrics {
  total_jobs: number;
  pending_jobs: number;
  processing_jobs: number;
  completed_jobs: number;
  failed_jobs: number;
  avg_processing_time_seconds: number;
  throughput_per_minute: number;
}

interface PerformanceStats {
  period_hours: number;
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  avg_response_time_ms: number;
  error_rate: number;
}

interface MonitoringDashboard {
  timestamp: string;
  current_metrics?: QueueMetrics;
  performance?: PerformanceStats;
  performance_history?: {
    period_hours?: number;
    data_points?: number;
    total_jobs: number;
    completed_jobs: number;
    failed_jobs: number;
    success_rate: number;
    avg_queue_depth: number;
    max_queue_depth: number;
    min_queue_depth: number;
    avg_jobs_per_minute: number;
    peak_jobs_per_minute: number;
    avg_processing_time: number;
    max_processing_time: number;
    min_processing_time: number;
    avg_failure_rate: number;
    max_failure_rate: number;
    time_series: Array<{
      timestamp: string;
      queue_depth: number;
      jobs_per_minute: number;
      processing_time: number;
      failure_rate: number;
      active_workers: number;
    }>;
    failure_trend: Array<{
      hour: string;
      avg_failure_rate: number;
      max_failure_rate: number;
    }>;
  };
  alerts: Array<{
    type: string;
    message: string;
    severity: 'low' | 'medium' | 'high';
    timestamp: string;
  }>;
  tier_metrics?: any;
  cache_metrics?: any;
  worker_utilization?: any;
  processing_time_distribution?: any;
  security_overview?: any;
  api_quota?: any;
}

export const useMonitoringDashboard = () => {
  const { user } = useAuth();

  return useQuery<MonitoringDashboard>({
    queryKey: ['monitoring', 'dashboard'],
    queryFn: async () => {
      const response = await fetch(`${BACKEND_URL}/monitoring/metrics/dashboard`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch monitoring dashboard');
      }

      return response.json();
    },
    enabled: !!user,
    refetchInterval: 60000, // Refetch every minute
    staleTime: 30000, // Consider data stale after 30 seconds
  });
};

export const usePerformanceMetrics = (hours: number = 24) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['monitoring', 'performance', hours],
    queryFn: async () => {
      const response = await fetch(
        `${BACKEND_URL}/monitoring/metrics/performance?hours=${hours}`,
        {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch performance metrics');
      }

      return response.json();
    },
    enabled: !!user,
    refetchInterval: 60000,
    staleTime: 30000,
  });
};

export const useDetailedHealth = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['monitoring', 'health'],
    queryFn: async () => {
      const response = await fetch(`${BACKEND_URL}/monitoring/health/detailed`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch health status');
      }

      return response.json();
    },
    enabled: !!user,
    refetchInterval: 30000, // Every 30 seconds
    staleTime: 15000,
  });
};

export const useStorageMetrics = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['monitoring', 'storage'],
    queryFn: async () => {
      const response = await fetch(`${BACKEND_URL}/monitoring/metrics/storage`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch storage metrics');
      }

      return response.json();
    },
    enabled: !!user,
    refetchInterval: 300000, // Every 5 minutes (storage doesn't change as rapidly)
    staleTime: 120000, // Consider stale after 2 minutes
  });
};
